// Vector graphics renderer
// Draws everything with that classic arcade vector look

import { Vec2, VectorShape } from "../utils/types";

interface BatchedLine {
  x1: number;
  y1: number;
  x2: number;
  y2: number;
}

export class VectorRenderer {
  private ctx: CanvasRenderingContext2D;
  private width: number;
  private height: number;

  // Default color is that classic green but we can change it per-draw
  private defaultColor = "#0f0";
  private defaultLineWidth = 1.5;

  // Batching system - group lines by style to reduce draw calls
  private batchMap: Map<string, BatchedLine[]> = new Map();
  private isBatching = false;

  // Track current state to avoid redundant changes
  private currentColor = "";
  private currentLineWidth = 0;

  // Reusable point for shape transforms (avoid allocations)
  private transformedPoints: Vec2[] = [];
  // Reusable points for circle drawing
  private circlePoints: Vec2[] = [];

  constructor(canvas: HTMLCanvasElement) {
    const ctx = canvas.getContext("2d");
    if (!ctx) {
      throw new Error("Could not get 2D context - this shouldnt happen");
    }
    this.ctx = ctx;
    this.width = canvas.width;
    this.height = canvas.height;

    // Pre-allocate transform buffer for typical shape sizes
    for (let i = 0; i < 20; i++) {
      this.transformedPoints.push({ x: 0, y: 0 });
    }
  }

  // Begin batching - lines will be queued instead of drawn immediately
  beginBatch(): void {
    this.isBatching = true;
  }

  // End batching - draw all queued lines grouped by style
  endBatch(): void {
    if (!this.isBatching) return;
    this.isBatching = false;

    // Draw each batch with a single stroke() call per style
    for (const [key, lines] of this.batchMap) {
      if (lines.length === 0) continue;

      const [color, widthStr] = key.split("|");
      const lineWidth = parseFloat(widthStr);

      this.setStyle(color, lineWidth);
      this.ctx.beginPath();

      for (let i = 0; i < lines.length; i++) {
        const line = lines[i];
        this.ctx.moveTo(line.x1, line.y1);
        this.ctx.lineTo(line.x2, line.y2);
      }

      this.ctx.stroke();
      lines.length = 0; // Clear for reuse
    }
  }

  // Set stroke style only if changed
  private setStyle(color: string, lineWidth: number): void {
    if (this.currentColor !== color) {
      this.ctx.strokeStyle = color;
      this.currentColor = color;
    }
    if (this.currentLineWidth !== lineWidth) {
      this.ctx.lineWidth = lineWidth;
      this.currentLineWidth = lineWidth;
    }
  }

  // Invalidate cached state (call after ctx.save/restore)
  invalidateState(): void {
    this.currentColor = "";
    this.currentLineWidth = 0;
  }

  // Get or create batch array for a style
  private getBatch(color: string, lineWidth: number): BatchedLine[] {
    // Round line width to 1 decimal place to avoid infinite key variations
    const roundedWidth = Math.round(lineWidth * 10) / 10;
    const key = `${color}|${roundedWidth}`;
    let batch = this.batchMap.get(key);
    if (!batch) {
      batch = [];
      this.batchMap.set(key, batch);
    }
    return batch;
  }

  clear(): void {
    // Full clear to prevent ghosting on HDR displays
    this.ctx.fillStyle = "#000";
    this.ctx.fillRect(0, 0, this.width, this.height);
    // Reset state tracking after clear
    this.currentColor = "";
    this.currentLineWidth = 0;
  }

  clearFull(): void {
    this.ctx.fillStyle = "#000";
    this.ctx.fillRect(0, 0, this.width, this.height);
    this.currentColor = "";
    this.currentLineWidth = 0;
  }

  // Draw a line between two points
  drawLine(start: Vec2, end: Vec2, color?: string, lineWidth?: number): void {
    const c = color ?? this.defaultColor;
    const w = lineWidth ?? this.defaultLineWidth;

    if (this.isBatching) {
      // Queue for batched drawing
      const batch = this.getBatch(c, w);
      batch.push({ x1: start.x, y1: start.y, x2: end.x, y2: end.y });
    } else {
      // Immediate draw with state tracking
      this.setStyle(c, w);
      this.ctx.beginPath();
      this.ctx.moveTo(start.x, start.y);
      this.ctx.lineTo(end.x, end.y);
      this.ctx.stroke();
    }
  }

  // Draw a shape from an array of points (optimized to avoid allocations)
  drawShape(
    shape: VectorShape,
    position: Vec2,
    rotation: number = 0,
    scale: number = 1,
    color?: string,
    lineWidth?: number,
  ): void {
    const points = shape.points;
    if (points.length < 2) return;

    // Ensure transform buffer is large enough
    while (this.transformedPoints.length < points.length) {
      this.transformedPoints.push({ x: 0, y: 0 });
    }

    // Pre-compute trig for rotation
    const cos = Math.cos(rotation);
    const sin = Math.sin(rotation);
    const posX = position.x;
    const posY = position.y;

    // Transform points in-place to reusable buffer
    for (let i = 0; i < points.length; i++) {
      const p = points[i];
      // Scale
      const sx = p.x * scale;
      const sy = p.y * scale;
      // Rotate and translate
      this.transformedPoints[i].x = posX + sx * cos - sy * sin;
      this.transformedPoints[i].y = posY + sx * sin + sy * cos;
    }

    const c = color ?? this.defaultColor;
    const w = lineWidth ?? this.defaultLineWidth;
    this.setStyle(c, w);

    this.ctx.beginPath();
    this.ctx.moveTo(this.transformedPoints[0].x, this.transformedPoints[0].y);

    for (let i = 1; i < points.length; i++) {
      this.ctx.lineTo(this.transformedPoints[i].x, this.transformedPoints[i].y);
    }

    if (shape.closed) {
      this.ctx.closePath();
    }

    this.ctx.stroke();
  }

  // Draw a polygon (convenience method for closed shapes)
  drawPolygon(points: Vec2[], color?: string, lineWidth?: number): void {
    this.drawShape(
      { points, closed: true },
      { x: 0, y: 0 },
      0,
      1,
      color,
      lineWidth,
    );
  }

  // Draw a circle using line segments (vector style)
  drawCircle(
    center: Vec2,
    radius: number,
    segments: number = 16,
    color?: string,
  ): void {
    // Ensure circlePoints array is large enough
    while (this.circlePoints.length < segments) {
      this.circlePoints.push({ x: 0, y: 0 });
    }

    // Update points in place
    for (let i = 0; i < segments; i++) {
      const angle = (i / segments) * Math.PI * 2;
      this.circlePoints[i].x = center.x + Math.cos(angle) * radius;
      this.circlePoints[i].y = center.y + Math.sin(angle) * radius;
    }

    // Draw using only the needed portion of the array
    const c = color ?? this.defaultColor;
    this.setStyle(c, this.defaultLineWidth);

    this.ctx.beginPath();
    this.ctx.moveTo(this.circlePoints[0].x, this.circlePoints[0].y);
    for (let i = 1; i < segments; i++) {
      this.ctx.lineTo(this.circlePoints[i].x, this.circlePoints[i].y);
    }
    this.ctx.closePath();
    this.ctx.stroke();
  }

  // Draw text in vector style (using canvas text but styled to match)
  drawText(
    text: string,
    position: Vec2,
    size: number = 16,
    color?: string,
    align: CanvasTextAlign = "left",
  ): void {
    this.ctx.font = `${size}px "Courier New", monospace`;
    this.ctx.fillStyle = color ?? this.defaultColor;
    this.ctx.textAlign = align;
    this.ctx.fillText(text, position.x, position.y);
  }

  // Add glow effect for that authentic vector look
  setGlow(blur: number, color?: string): void {
    this.ctx.shadowBlur = blur;
    this.ctx.shadowColor = color ?? this.defaultColor;
  }

  clearGlow(): void {
    this.ctx.shadowBlur = 0;
  }

  getWidth(): number {
    return this.width;
  }

  getHeight(): number {
    return this.height;
  }

  // ============================================
  // Editor Helper Methods
  // ============================================

  // Get the raw canvas context for editor-specific drawing (selection boxes, handles, etc.)
  getContext(): CanvasRenderingContext2D {
    return this.ctx;
  }

  // Update dimensions (call after canvas resize)
  updateDimensions(width: number, height: number): void {
    this.width = width;
    this.height = height;
  }

  // Set color directly (useful for editors)
  setColor(color: string): void {
    this.defaultColor = color;
    this.currentColor = ""; // Force update on next draw
  }

  // Set line width directly (useful for editors)
  setLineWidth(lineWidth: number): void {
    this.defaultLineWidth = lineWidth;
    this.currentLineWidth = 0; // Force update on next draw
  }

  // Get current default color
  getColor(): string {
    return this.defaultColor;
  }

  // Get current default line width
  getLineWidth(): number {
    return this.defaultLineWidth;
  }

  // Draw a rectangle (for selection boxes, bounds, etc.)
  drawRect(
    x: number,
    y: number,
    width: number,
    height: number,
    color?: string,
    lineWidth?: number,
  ): void {
    const c = color ?? this.defaultColor;
    const w = lineWidth ?? this.defaultLineWidth;
    this.setStyle(c, w);

    this.ctx.beginPath();
    this.ctx.rect(x, y, width, height);
    this.ctx.stroke();
  }

  // Draw a filled rectangle
  fillRect(
    x: number,
    y: number,
    width: number,
    height: number,
    color?: string,
  ): void {
    this.ctx.fillStyle = color ?? this.defaultColor;
    this.ctx.fillRect(x, y, width, height);
  }

  // Draw a dashed line (for guides, selection previews)
  drawDashedLine(
    start: Vec2,
    end: Vec2,
    dashPattern: number[] = [5, 5],
    color?: string,
    lineWidth?: number,
  ): void {
    const c = color ?? this.defaultColor;
    const w = lineWidth ?? this.defaultLineWidth;
    this.setStyle(c, w);

    this.ctx.setLineDash(dashPattern);
    this.ctx.beginPath();
    this.ctx.moveTo(start.x, start.y);
    this.ctx.lineTo(end.x, end.y);
    this.ctx.stroke();
    this.ctx.setLineDash([]);
  }

  // Draw a dashed rectangle (for selection boxes)
  drawDashedRect(
    x: number,
    y: number,
    width: number,
    height: number,
    dashPattern: number[] = [5, 5],
    color?: string,
    lineWidth?: number,
  ): void {
    const c = color ?? this.defaultColor;
    const w = lineWidth ?? this.defaultLineWidth;
    this.setStyle(c, w);

    this.ctx.setLineDash(dashPattern);
    this.ctx.beginPath();
    this.ctx.rect(x, y, width, height);
    this.ctx.stroke();
    this.ctx.setLineDash([]);
  }

  // Draw a filled circle (for vertex handles, anchor points)
  fillCircle(center: Vec2, radius: number, color?: string): void {
    this.ctx.fillStyle = color ?? this.defaultColor;
    this.ctx.beginPath();
    this.ctx.arc(center.x, center.y, radius, 0, Math.PI * 2);
    this.ctx.fill();
  }

  // Draw a cross/plus marker (for origin, anchor points)
  drawCross(
    center: Vec2,
    size: number,
    color?: string,
    lineWidth?: number,
  ): void {
    const halfSize = size / 2;
    this.drawLine(
      { x: center.x - halfSize, y: center.y },
      { x: center.x + halfSize, y: center.y },
      color,
      lineWidth,
    );
    this.drawLine(
      { x: center.x, y: center.y - halfSize },
      { x: center.x, y: center.y + halfSize },
      color,
      lineWidth,
    );
  }

  // Draw a diamond shape (for keyframe markers)
  drawDiamond(
    center: Vec2,
    size: number,
    color?: string,
    lineWidth?: number,
  ): void {
    const halfSize = size / 2;
    const points: Vec2[] = [
      { x: center.x, y: center.y - halfSize },
      { x: center.x + halfSize, y: center.y },
      { x: center.x, y: center.y + halfSize },
      { x: center.x - halfSize, y: center.y },
    ];
    this.drawPolygon(points, color, lineWidth);
  }

  // Fill a diamond shape
  fillDiamond(center: Vec2, size: number, color?: string): void {
    const halfSize = size / 2;
    this.ctx.fillStyle = color ?? this.defaultColor;
    this.ctx.beginPath();
    this.ctx.moveTo(center.x, center.y - halfSize);
    this.ctx.lineTo(center.x + halfSize, center.y);
    this.ctx.lineTo(center.x, center.y + halfSize);
    this.ctx.lineTo(center.x - halfSize, center.y);
    this.ctx.closePath();
    this.ctx.fill();
  }

  // Clear with a specific color (for editor backgrounds)
  clearWithColor(color: string): void {
    this.ctx.fillStyle = color;
    this.ctx.fillRect(0, 0, this.width, this.height);
    this.currentColor = "";
    this.currentLineWidth = 0;
  }

  // Save canvas state
  save(): void {
    this.ctx.save();
  }

  // Restore canvas state
  restore(): void {
    this.ctx.restore();
    this.invalidateState();
  }

  // Translate canvas
  translate(x: number, y: number): void {
    this.ctx.translate(x, y);
  }

  // Scale canvas
  scale(x: number, y: number): void {
    this.ctx.scale(x, y);
  }

  // Rotate canvas
  rotate(angle: number): void {
    this.ctx.rotate(angle);
  }

  // Set global alpha
  setAlpha(alpha: number): void {
    this.ctx.globalAlpha = alpha;
  }

  // Reset global alpha
  resetAlpha(): void {
    this.ctx.globalAlpha = 1;
  }
}
